"use client";

import React, { useEffect, useRef } from "react";
import { ArrowRight, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FutureReadyInfrastructure() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Particle[] = [];
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 1.5 + 0.5;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > canvas!.width) this.vx = -this.vx;
        if (this.y < 0 || this.y > canvas!.height) this.vy = -this.vy;
      }
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(45, 127, 249, 0.4)";
        ctx.fill();
      }
    }
    const initParticles = () => {
      particles = [];
      const numberOfParticles = Math.floor(canvas.width * canvas.height / 15000);
      for (let i = 0; i < numberOfParticles; i++) {
        particles.push(new Particle());
      }
    };
    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(45, 127, 249, ${0.15 - distance / 800})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    animate();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section data-section-id="161544" id="automated-growth-systems" className="dark bg-background text-foreground relative min-h-screen flex items-center overflow-hidden pt-24 pb-16 lg:pt-0 lg:pb-0">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes float-rotate {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        .animate-float-rotate {
          animation: float-rotate 8s ease-in-out infinite;
        }
      `
    }} />

      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0 opacity-40 pointer-events-none" aria-hidden="true" />

      {}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[150px] pointer-events-none z-0" />
      <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-primary/5 rounded-full blur-[150px] pointer-events-none z-0" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={contentRef} className={`max-w-2xl transition-all duration-1000 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Badge variant="outline" className="mb-6 border-emerald-500/30 bg-emerald-500/5 text-emerald-500 backdrop-blur-sm px-3 py-1.5 text-sm font-default">
              <span className="relative flex size-2.5 mr-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full size-2.5 bg-emerald-500"></span>
              </span>
              Growth Engine: Online
            </Badge>

            <h1 className="text-[clamp(3rem,6vw,80px)] font-default font-bold leading-[1.1] tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-slate-400">
              Automated Growth Systems
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl leading-relaxed font-default">Scale your business with AGS-powered AI automation, expert consulting, and high-performance SaaS models designed for rapid growth.</p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="https://links.agssuite.com/widget/form/0kYf5fvvvIeS2gTSy86O">
                <Button size="lg" className="h-14 px-8 text-base font-default font-semibold group relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(191,255,0,0.3)]">
                  <span className="relative z-10 flex items-center gap-2">
                    Get Started Now
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />
                </Button>
              </Link>
              
              <div className="flex items-center gap-3 text-sm text-muted-foreground px-4 py-2 rounded-md bg-card/30 border border-border/50 backdrop-blur-sm cursor-pointer hover:bg-card/50 transition-colors">
                <Activity className="size-4 text-secondary" />
                <span className="font-default">Watch Demo</span>
              </div>
            </div>
          </div>

          {}
          <div className="relative hidden lg:flex justify-center items-center h-[600px]">
            {}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 bg-secondary/10 rounded-full blur-[80px] animate-pulse" />
            </div>
            
            {}
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623996/img/abstract_cyber_core_3d.png" alt="AGS Growth Engine Visualization" className="relative z-10 w-full max-w-lg object-contain animate-float-rotate drop-shadow-[0_0_40px_rgba(45,127,249,0.2)]" />
            
            {}
            <div className="absolute top-1/4 right-10 bg-card/60 backdrop-blur-md border border-border/50 p-3 rounded-lg shadow-2xl animate-[bounce_4s_infinite_ease-in-out]">
              <div className="text-xs text-muted-foreground mb-1 font-default">Conversion Rate</div>
              <div className="text-sm font-mono text-primary">94% Increase</div>
            </div>
            
            <div className="absolute bottom-1/3 left-0 bg-card/60 backdrop-blur-md border border-border/50 p-3 rounded-lg shadow-2xl animate-[bounce_5s_infinite_ease-in-out_reverse]">
              <div className="text-xs text-muted-foreground mb-1 font-default">ROI Multiplier</div>
              <div className="text-sm font-mono text-emerald-500">5.5x Growth</div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}